import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetVehicleServiceReportQuery } from '../../../../api/endpoints/vehicleServiceReport';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const INITIAL_FILTER_STATE = {
    marka: null,
    markaMatchMode: FilterMatchMode.CONTAINS,
    ay: null,
    ayMatchMode: FilterMatchMode.CONTAINS,
    adet: null,
    adetMatchMode: FilterMatchMode.CONTAINS,
    bayiAdi: null,
    bayiAdiMatchMode: FilterMatchMode.CONTAINS,
    bayiKodu: null,
    bayiKoduMatchMode: FilterMatchMode.CONTAINS
};

const backendColumnMap = {
    marka: 'Marka',
    ay: 'Ay',
    adet: 'Adet',
    bayiAdi: 'BayiAdi',
    bayiKodu: 'BayiKodu'
};

const TEXT_FIELDS = ['marka', 'ay', 'adet', 'bayiAdi', 'bayiKodu'];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState[field];
        const matchMode = filtersState[`${field}MatchMode`];
        const column = backendColumnMap[field];
        if (!column || value === null || value === undefined || value === '') return;       
        filters.push({
            column,
            operator: matchModeToOperator[matchMode] ?? 1,
            value: typeof value === 'string' ? value.trim() : String(value).trim()
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useVehicleServiceReportData = () => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        page: 1
    });

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);

    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows
        };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;

        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const { data: apiData, isFetching } = useGetVehicleServiceReportQuery(queryParams);

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams({ first: 0, rows: 20, page: 1 });
    };

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const handleSortChange = (event) => {
        setSortField(event?.sortField || null);
        setSortOrder(event?.sortOrder || 0);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const exportParams = useMemo(() => {
        const { pageNumber, pageSize, ...rest } = queryParams;
        return rest;
    }, [queryParams]);

    return {
        apiData,
        isLoading: isFetching, 
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters,
        exportParams
    };
};

export default useVehicleServiceReportData;